/**
 * Şeffaf Yayın Analytics — detay sayfası.
 *
 * Yayınlanan postların GERÇEK etkileşimi (likes/RT/reply/quote/bookmark/impressions).
 * Kaynak: hype_memory.performance_log (6h poller) → GET /hype/analytics/engagement.
 * DÜRÜST tasarım: 0-etkileşim postlar gizlenmez; vanity grafik YOK, gerçek defter.
 * Koyu bento UI (DashboardHome ile aynı tokenlar) — beyaz tema YOK.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, ArrowLeft, Heart, Repeat2, MessageSquare, Quote,
  Bookmark, BarChart3, ExternalLink, ArrowUpRight, ArrowDownRight, Percent,
  Users, Eye, Play, Clock,
} from "lucide-react";
import { FaXTwitter, FaLinkedinIn, FaInstagram, FaYoutube } from "react-icons/fa6";
import { HiDocumentText } from "react-icons/hi2";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
} from "recharts";
import { useAccount } from "@/contexts/AccountContext";
import { fetchEngagementAnalytics, fetchChannelAnalytics } from "@/services/hypeService";
import { cn } from "@/lib/utils";

// Kanal metrik etiketleri (TR) + ikon — yalnız dönen metrikler gösterilir.
const METRIC_META = {
  followers: { label: "Yeni takipçi", icon: Users },
  reach: { label: "Erişim", icon: Eye },
  views: { label: "Görüntülenme", icon: Play },
  likes: { label: "Beğeni", icon: Heart },
  comments: { label: "Yorum", icon: MessageSquare },
  shares: { label: "Paylaşım", icon: Repeat2 },
  saves: { label: "Kaydetme", icon: Bookmark },
  quotes: { label: "Alıntı", icon: Quote },
  subscribers_gained: { label: "Abone +", icon: Users },
  subscribers_lost: { label: "Abone −", icon: Users },
  estimated_minutes_watched: { label: "İzlenme dk", icon: Clock },
  average_view_duration: { label: "Ort. süre (sn)", icon: Clock },
  average_view_percentage: { label: "Ort. izlenme %", icon: Percent },
};
const METRIC_ORDER = [
  "followers", "reach", "views", "likes", "comments", "shares", "saves", "quotes",
  "subscribers_gained", "subscribers_lost", "estimated_minutes_watched",
  "average_view_duration", "average_view_percentage",
];

function ChannelCard({ ch, selected, onClick }) {
  const platform = (ch.platform || "").toLowerCase();
  const Icon = platformIcon(platform);
  const color = PLATFORM_COLOR[platform] || "#22d3ee";
  const m = ch.metrics || {};
  // Yalnız sıfır-olmayan metrikler (düşükleri gizle — Berkay).
  const keys = METRIC_ORDER.filter((k) => k in m && (m[k] ?? 0) > 0);
  Object.keys(m).forEach((k) => {
    if (!k.endsWith("_pct_change") && !keys.includes(k) && (m[k] ?? 0) > 0) keys.push(k);
  });
  const active = (ch.platform || "").toLowerCase() === (selected || "");
  return (
    <div
      onClick={onClick}
      className={cn(
        "bento-card relative rounded-2xl p-4 overflow-hidden bg-card/80 backdrop-blur-sm border transition-colors",
        onClick && "cursor-pointer hover:border-cyan-500/40",
        active ? "border-cyan-500/50" : "border-border/40"
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: color + "22" }}>
          <Icon className="h-4 w-4" style={{ color }} />
        </div>
        <span className="text-sm font-semibold capitalize">{ch.username || platform}</span>
        <span className="text-[10px] text-muted-foreground/40 ml-auto capitalize">{platform}</span>
      </div>
      <div className="space-y-1.5">
        {keys.map((k) => {
          const meta = METRIC_META[k] || { label: k.replace(/_/g, " "), icon: Activity };
          const MIcon = meta.icon;
          const pct = m[`${k}_pct_change`];
          return (
            <div key={k} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-muted-foreground/70">
                <MIcon className="h-3.5 w-3.5" />{meta.label}
              </span>
              <span className="flex items-center gap-1">
                <span className="font-semibold tabular-nums">{(m[k] ?? 0).toLocaleString("tr-TR")}</span>
                {pct != null && pct !== 0 && (
                  <span className={cn("text-[9px] flex items-center", pct > 0 ? "text-emerald-500" : "text-rose-500")}>
                    {pct > 0 ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownRight className="h-2.5 w-2.5" />}
                    {Math.abs(pct)}%
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PLATFORM_COLOR = {
  twitter: "#7dd3fc", x: "#7dd3fc", linkedin: "#0a66c2",
  instagram: "#e1306c", youtube: "#ef4444", devto: "#a78bfa", blog: "#f59e0b",
};
const GRID = "rgba(255,255,255,0.08)";
const AXIS = "rgba(255,255,255,0.35)";

/* Ortak koyu tema tooltip (repo deseni: #1A1A1A + border-white/10) */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-xl px-3 py-2 shadow-2xl">
      {label && <p className="text-[11px] text-white/50 mb-1">{label}</p>}
      {payload.map((e, i) => (
        <p key={i} className="text-xs font-semibold" style={{ color: e.color || e.payload?.color || "#fff" }}>
          {e.name}: {(e.value ?? 0).toLocaleString("tr-TR")}
        </p>
      ))}
    </div>
  );
}

const dayTick = (d) => {
  const parts = String(d).split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}` : d;
};

/* Grafik kabuğu — bento kart + başlık */
function ChartCard({ title, children, className }) {
  return (
    <div className={cn("bento-card relative rounded-2xl md:rounded-3xl p-4 md:p-5 overflow-hidden bg-card/80 backdrop-blur-sm border border-border/40", className)}>
      <p className="text-xs font-semibold text-muted-foreground mb-3">{title}</p>
      {children}
    </div>
  );
}

const PLATFORM_ICON = {
  twitter: FaXTwitter, x: FaXTwitter, linkedin: FaLinkedinIn,
  instagram: FaInstagram, youtube: FaYoutube, devto: HiDocumentText,
  blog: HiDocumentText,
};

function platformIcon(p) {
  return PLATFORM_ICON[(p || "").toLowerCase()] || Activity;
}

function relFromIso(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const h = Math.round((Date.now() - t) / 3600000);
  if (h < 1) return "az önce";
  if (h < 24) return `${h}s önce`;
  return `${Math.round(h / 24)}g önce`;
}

/* Tek metrik rozeti — sayı + ufak yüzde değişimi */
function Metric({ icon: Icon, value, pct, muted }) {
  return (
    <div className={cn("flex items-center gap-1", muted && "opacity-40")}>
      <Icon className="h-3.5 w-3.5 text-muted-foreground/70" />
      <span className="text-xs font-semibold tabular-nums">{value ?? 0}</span>
      {pct != null && pct !== 0 && (
        <span className={cn(
          "text-[9px] flex items-center",
          pct > 0 ? "text-emerald-500" : "text-rose-500"
        )}>
          {pct > 0 ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownRight className="h-2.5 w-2.5" />}
          {Math.abs(pct)}%
        </span>
      )}
    </div>
  );
}

/* Üst özet kartı (küçük) */
function SummaryTile({ icon: Icon, value, label, accent }) {
  return (
    <div className="bento-card relative rounded-2xl p-4 overflow-hidden bg-card/80 backdrop-blur-sm border border-border/40">
      <div className="flex items-center gap-2 mb-2">
        <div className={cn("h-8 w-8 rounded-xl flex items-center justify-center bg-gradient-to-br", accent)}>
          <Icon className="h-4 w-4 text-white" />
        </div>
      </div>
      <p className="font-outfit text-2xl font-bold tracking-tight tabular-nums">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

// Animasyonlu sayaç (DashboardHome deseni).
function AnimatedCounter({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) { setDisplay(0); return; }
    let start = 0;
    const step = value / (duration / 16);
    const t = setInterval(() => {
      start += step;
      if (start >= value) { setDisplay(value); clearInterval(t); }
      else setDisplay(Math.floor(start));
    }, 16);
    return () => clearInterval(t);
  }, [value, duration]);
  return <span>{(display ?? 0).toLocaleString("tr-TR")}</span>;
}

// Anlık (toplanmayan) metrikler — headline magnitude yarışına sokma.
const _POINT_IN_TIME = (k) =>
  k.startsWith("average") || k.includes("percentage") || k.includes("duration");

// Kanal için: gösterilebilir (sıfır-olmayan) metrikler, headline (en büyük), grafik metriği (en çok noktalı).
function channelInfo(ch) {
  const m = ch.metrics || {};
  const series = ch.series || {};
  const keys = Object.keys(m).filter((k) => !k.endsWith("_pct_change") && (m[k] ?? 0) > 0);
  const sumKeys = keys.filter((k) => !_POINT_IN_TIME(k));
  const pool = sumKeys.length ? sumKeys : keys;
  const headlineKey = pool.slice().sort((a, b) => (m[b] || 0) - (m[a] || 0))[0] || null;
  const seriesLen = (k) => (Array.isArray(series[k]) ? series[k].length : 0);
  // Çizilebilir = ≥2 günlük nokta (Postiz çoğu metrikte tek değer veriyor).
  const chartable = keys.filter((k) => seriesLen(k) >= 2);
  // Default grafik metriği = çizilebilir + en büyük toplam (Erişim gibi); yoksa headline.
  const chartMetric = chartable.slice().sort((a, b) => (m[b] || 0) - (m[a] || 0))[0] || headlineKey;
  // sıralı gösterilecek metrik anahtarları (METRIC_ORDER önce)
  const orderedKeys = [
    ...METRIC_ORDER.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !METRIC_ORDER.includes(k)),
  ];
  return {
    headlineKey, headlineVal: headlineKey ? m[headlineKey] : 0,
    keys: orderedKeys, chartMetric, chartable: new Set(chartable),
  };
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { effectiveAccountId } = useAccount();
  const days = 30;  // sabit pencere — Postiz kanal verisi 30 günlük (toggle kaldırıldı)
  const [data, setData] = useState(null);
  const [channels, setChannels] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const load = useCallback(async (silent) => {
    if (!effectiveAccountId) return;
    if (!silent) setLoading(true);
    try {
      const [res, ch] = await Promise.allSettled([
        fetchEngagementAnalytics(effectiveAccountId, days),
        fetchChannelAnalytics(effectiveAccountId),
      ]);
      setData(res.status === "fulfilled" ? res.value : { posts: [], summary: {}, days });
      if (ch.status === "fulfilled") setChannels(ch.value);
    } finally {
      setLoading(false);
    }
  }, [effectiveAccountId, days]);

  // ilk yük + days değişimi
  useEffect(() => { load(false); }, [load]);
  // "sürekli güncellenmeli": 60sn sessiz auto-refresh + sekmeye dönünce yenile
  useEffect(() => {
    const t = setInterval(() => load(true), 60000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const summary = data?.summary || {};
  const posts = useMemo(() => data?.posts || [], [data]);
  const timeseries = data?.timeseries || [];
  const fmt = (n) => (n ?? 0).toLocaleString("tr-TR");
  const ratePct = summary.avg_engagement_rate != null
    ? `%${(summary.avg_engagement_rate * 100).toFixed(1)}` : "—";

  // Platform karşılaştırma (posts'tan türetilir) — 5 platform tek bakışta
  const byPlatform = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const k = (p.platform || "?").toLowerCase();
      const e = map[k] || { platform: k, impressions: 0, engagements: 0, posts: 0, withLink: 0, measured: 0, last: null };
      e.impressions += p.metrics?.impressions || 0;
      e.engagements += p.engagements || 0;
      e.posts += 1;
      if (p.release_url) e.withLink += 1;
      if (p.measured) e.measured += 1;
      if (p.published_at && (!e.last || p.published_at > e.last)) e.last = p.published_at;
      map[k] = e;
    });
    return Object.values(map).sort((a, b) => b.posts - a.posts);
  }, [posts]);

  // Radar "footprint": platformlar arası gönderi hacmi karşılaştırması
  const radarData = useMemo(
    () => byPlatform.map((e) => ({ platform: e.platform, Gönderi: e.posts })),
    [byPlatform],
  );

  // Etkileşim kırılımı (donut)
  const composition = useMemo(() => {
    const acc = { likes: 0, retweets: 0, replies: 0, quotes: 0, bookmarks: 0 };
    posts.forEach((p) => {
      const m = p.metrics || {};
      acc.likes += m.likes || 0; acc.retweets += m.retweets || 0;
      acc.replies += m.replies || 0; acc.quotes += m.quotes || 0;
      acc.bookmarks += m.bookmarks || 0;
    });
    return [
      { name: "Beğeni", value: acc.likes, color: "#f43f5e" },
      { name: "Repost", value: acc.retweets, color: "#10b981" },
      { name: "Yanıt", value: acc.replies, color: "#0ea5e9" },
      { name: "Alıntı", value: acc.quotes, color: "#f59e0b" },
      { name: "Kayıt", value: acc.bookmarks, color: "#8b5cf6" },
    ].filter((d) => d.value > 0);
  }, [posts]);

  const tsHasData = timeseries.some((d) => d.posts > 0 || d.impressions > 0);

  // ── Kanal-öncelikli ── kanalları headline magnitude'a göre sırala
  const channelsRanked = useMemo(() => {
    const list = channels?.channels || [];
    return list
      .map((ch) => ({ ch, ...channelInfo(ch) }))
      .filter((e) => e.headlineKey)   // hiç sıfır-olmayan metriği olmayan kanalı atla
      .sort((a, b) => (b.headlineVal || 0) - (a.headlineVal || 0));
  }, [channels]);

  // default seçili kanal = en büyük
  useEffect(() => {
    if (channelsRanked.length && !channelsRanked.some((e) => e.ch.platform === selectedPlatform)) {
      setSelectedPlatform(channelsRanked[0].ch.platform);
    }
  }, [channelsRanked, selectedPlatform]);

  const selectedEntry = useMemo(
    () => channelsRanked.find((e) => e.ch.platform === selectedPlatform) || channelsRanked[0] || null,
    [channelsRanked, selectedPlatform],
  );

  // platform değişince grafik metriğini default'a çek
  useEffect(() => {
    if (selectedEntry) setSelectedMetric(selectedEntry.chartMetric);
  }, [selectedEntry]);

  // seçili kanal + metrik → grafik verisi (tarih sıralı, son N gün)
  const channelChart = useMemo(() => {
    if (!selectedEntry || !selectedMetric) return [];
    const arr = (selectedEntry.ch.series || {})[selectedMetric] || [];
    let rows = arr
      .filter((p) => p && p.date)
      .map((p) => ({ date: p.date, total: Number(p.total) || 0 }))
      .sort((a, b) => a.date.localeCompare(b.date));
    if (days && rows.length > days) rows = rows.slice(-days);
    return rows;
  }, [selectedEntry, selectedMetric, days]);

  const chSummary = channels?.summary || {};

  return (
    <div className="max-w-6xl mx-auto px-0 sm:px-1">
      {/* Başlık + geri */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate("/dashboard")}
          className="h-9 w-9 rounded-xl flex items-center justify-center bg-card/60 border border-border/40 hover:bg-card/90 transition-colors"
          aria-label="Geri"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-600 flex items-center justify-center">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-outfit text-xl md:text-2xl font-bold tracking-tight">Yayın Performansı</h1>
            <p className="text-[12px] text-muted-foreground">
              {channelsRanked.length > 0
                ? `${channelsRanked.length} kanal · canlı performans`
                : "Kanal performansı + gönderi defteri"}
            </p>
          </div>
        </div>
        <span className="ml-auto text-[11px] text-muted-foreground/60 px-2.5 py-1 rounded-lg bg-card/60 border border-border/40">
          son 30 gün
        </span>
      </div>

      {/* ── KANAL ÖNCELİKLİ ── */}
      {channelsRanked.length > 0 ? (
        <>
          {/* Kanal seçici tab'lar */}
          <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
            {channelsRanked.map((e) => {
              const p = (e.ch.platform || "").toLowerCase();
              const Icon = platformIcon(p);
              const color = PLATFORM_COLOR[p] || "#22d3ee";
              const on = p === selectedPlatform;
              return (
                <button key={p} onClick={() => setSelectedPlatform(p)}
                  className={cn("flex items-center gap-2 px-3 py-1.5 rounded-xl border text-sm font-medium transition-colors shrink-0",
                    on ? "border-cyan-500/50 bg-cyan-500/10 text-foreground" : "border-border/40 bg-card/60 text-muted-foreground hover:bg-card/90")}>
                  <span className="h-5 w-5 rounded-md flex items-center justify-center" style={{ background: color + "22" }}>
                    <Icon className="h-3 w-3" style={{ color }} />
                  </span>
                  <span className="capitalize">{e.ch.username || p}</span>
                </button>
              );
            })}
          </div>

          {/* Büyük kanal grafiği + headline + metrik seçici */}
          {selectedEntry && (
            <ChartCard className="mb-4"
              title={`${selectedEntry.ch.username || selectedPlatform} · ${METRIC_META[selectedMetric]?.label || selectedMetric} — son ${days} gün`}>
              <div className="flex items-end gap-4 mb-3 flex-wrap">
                <div>
                  <p className="font-outfit text-4xl md:text-5xl font-bold tracking-tight tabular-nums">
                    <AnimatedCounter value={selectedEntry.ch.metrics?.[selectedEntry.headlineKey] || 0} />
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {METRIC_META[selectedEntry.headlineKey]?.label || selectedEntry.headlineKey}
                  </p>
                </div>
                <div className="flex gap-4 ml-auto">
                  {selectedEntry.keys.filter((k) => k !== selectedEntry.headlineKey).slice(0, 3).map((k) => (
                    <div key={k} className="text-right">
                      <p className="text-lg font-semibold tabular-nums">{(selectedEntry.ch.metrics[k] ?? 0).toLocaleString("tr-TR")}</p>
                      <p className="text-[10px] text-muted-foreground/70">{METRIC_META[k]?.label || k}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                {selectedEntry.keys.map((k) => {
                  const chartable = selectedEntry.chartable.has(k);
                  return (
                    <button key={k} onClick={() => setSelectedMetric(k)}
                      title={chartable ? undefined : "Postiz bu metrik için günlük kırılım vermiyor"}
                      className={cn("px-2 py-0.5 rounded-lg text-[11px] font-medium transition-colors",
                        selectedMetric === k ? "bg-cyan-500/15 text-cyan-500" : "bg-secondary/40 text-muted-foreground hover:text-foreground",
                        !chartable && "opacity-50")}>
                      {METRIC_META[k]?.label || k}{!chartable && " ·"}
                    </button>
                  );
                })}
              </div>
              {channelChart.length >= 2 ? (
                <div className="h-56 w-full" style={{ filter: "drop-shadow(0 0 8px rgba(34,211,238,0.12))" }}>
                  <ResponsiveContainer>
                    <AreaChart key={selectedPlatform + selectedMetric} data={channelChart} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="chGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={PLATFORM_COLOR[selectedPlatform] || "#22d3ee"} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={PLATFORM_COLOR[selectedPlatform] || "#22d3ee"} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={GRID} vertical={false} />
                      <XAxis dataKey="date" tickFormatter={dayTick} tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={16} />
                      <YAxis tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                      <RTooltip content={<ChartTooltip />} labelFormatter={dayTick} />
                      <Area type="monotone" dataKey="total" name={METRIC_META[selectedMetric]?.label || selectedMetric}
                        stroke={PLATFORM_COLOR[selectedPlatform] || "#22d3ee"} strokeWidth={2} fill="url(#chGrad)"
                        animationDuration={800} dot={channelChart.length < 8 ? { r: 3 } : false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-56 flex flex-col items-center justify-center gap-2 text-center">
                  <p className="font-outfit text-4xl font-bold tabular-nums">
                    {(selectedEntry.ch.metrics?.[selectedMetric] ?? 0).toLocaleString("tr-TR")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {METRIC_META[selectedMetric]?.label || selectedMetric} — toplam (son {days} gün)
                  </p>
                  <p className="text-[11px] text-muted-foreground/50 max-w-xs">
                    Postiz bu metrik için günlük kırılım vermiyor — yalnız tek değer.
                    Günlük çizgi için "Erişim" gibi işaretsiz metrikleri seç.
                  </p>
                </div>
              )}
            </ChartCard>
          )}

          {/* Kanal kartları (sıralı, tıklanabilir, sıfır metrikler gizli) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {channelsRanked.map((e) => (
              <ChannelCard key={e.ch.integration_id || e.ch.platform} ch={e.ch}
                selected={selectedPlatform}
                onClick={() => setSelectedPlatform((e.ch.platform || "").toLowerCase())} />
            ))}
          </div>

          {/* Kanal toplamları */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-2">
            <SummaryTile icon={Users} value={fmt(chSummary.total_followers)} label="Toplam takipçi" accent="from-violet-500/80 to-purple-600/80" />
            <SummaryTile icon={Eye} value={fmt(chSummary.total_reach)} label="Toplam erişim" accent="from-sky-500/80 to-blue-600/80" />
            <SummaryTile icon={Play} value={fmt(chSummary.total_views)} label="Toplam görüntülenme" accent="from-cyan-500/80 to-emerald-600/80" />
            <SummaryTile icon={Heart} value={fmt(chSummary.total_likes)} label="Toplam beğeni" accent="from-rose-500/80 to-pink-600/80" />
          </div>
          <p className="text-[10px] text-muted-foreground/40 mb-4 px-1">
            Kanal metrikleri Postiz'den 6 saatte bir toplanır. LinkedIn ve dev.to için Postiz analytics sağlamıyor.
            {channels?.as_of && ` Son ölçüm: ${new Date(channels.as_of).toLocaleString("tr-TR")}.`}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground/60 mb-4 px-1">
          Kanal verisi henüz toplanmadı (6 saatte bir güncellenir).
        </p>
      )}

      {/* Detaylar (kapalı) — eski gönderi-bazlı X defteri */}
      <button onClick={() => setShowDetails((v) => !v)}
        className="w-full flex items-center justify-center gap-2 py-2.5 mb-4 rounded-xl border border-border/40 bg-card/40 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-card/70 transition-colors">
        Gönderi bazlı detaylar (X) {showDetails ? "▲" : "▼"}
      </button>

      {showDetails && (
      <>
      {/* Özet şeridi */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
        <SummaryTile icon={BarChart3} value={fmt(summary.sum_impressions)} label="Toplam gösterim" accent="from-sky-500/80 to-blue-600/80" />
        <SummaryTile icon={Heart} value={fmt(summary.sum_likes)} label="Toplam beğeni" accent="from-rose-500/80 to-pink-600/80" />
        <SummaryTile icon={Activity} value={fmt(summary.sum_engagements)} label="Toplam etkileşim" accent="from-cyan-500/80 to-emerald-600/80" />
        <SummaryTile icon={Percent} value={ratePct} label="Ort. etkileşim oranı" accent="from-purple-500/80 to-indigo-600/80" />
      </div>

      {/* Dürüstlük notu — etkileşim yalnız X'te ölçülüyor */}
      <p className="text-[11px] text-muted-foreground/60 mb-4 px-1">
        Etkileşim ölçümü şu an yalnız X (Twitter) için — {summary.measured_posts || 0}/{summary.total_posts || 0} gönderi ölçülü.
        Diğer platformlar (LinkedIn, dev.to, Instagram, YouTube) gönderi olarak listelenir.
        {summary.zero_impression_count > 0 && ` ${summary.zero_impression_count} gönderi henüz gösterim almadı.`}
      </p>

      {/* ── Grafikler ── */}
      {/* 1. Zaman çizelgesi (gösterim + etkileşim) */}
      <ChartCard title={`Gönderi & gösterim — son ${days} gün`} className="mb-4">
        {tsHasData ? (
          <div className="h-56 w-full" style={{ filter: "drop-shadow(0 0 8px rgba(34,211,238,0.12))" }}>
            <ResponsiveContainer>
              <AreaChart data={timeseries} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tickFormatter={dayTick} tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={16} />
                <YAxis tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                <RTooltip content={<ChartTooltip />} labelFormatter={dayTick} />
                <Area type="monotone" dataKey="posts" name="Gönderi" stroke="#22d3ee" strokeWidth={2} fill="url(#impGrad)" animationDuration={800} />
                <Area type="monotone" dataKey="impressions" name="Gösterim (X)" stroke="#34d399" strokeWidth={2} fill="url(#engGrad)" animationDuration={800} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-24 flex items-center justify-center text-xs text-muted-foreground/60">
            Bu dönemde ölçülen gösterim/etkileşim yok — veri biriktikçe burada görünecek.
          </div>
        )}
      </ChartCard>

      {/* 2+3. Platform dağılımı + etkileşim kırılımı */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <ChartCard title="Platform dağılımı (gönderi)">
          {byPlatform.length > 0 ? (
            <div className="h-52 w-full">
              <ResponsiveContainer>
                <BarChart data={byPlatform} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="platform" tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: AXIS, fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                  <RTooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Bar dataKey="posts" name="Gönderi" radius={[6, 6, 0, 0]} animationDuration={800}>
                    {byPlatform.map((e, i) => (
                      <Cell key={i} fill={PLATFORM_COLOR[e.platform] || "#22d3ee"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-xs text-muted-foreground/60">Veri yok.</div>
          )}
        </ChartCard>

        <ChartCard title="Etkileşim kırılımı">
          {composition.length > 0 ? (
            <div className="h-52 w-full flex items-center">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={composition} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={72} paddingAngle={2} animationDuration={800}>
                    {composition.map((e, i) => (
                      <Cell key={i} fill={e.color} stroke="transparent" />
                    ))}
                  </Pie>
                  <RTooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-col gap-1.5 pr-2">
                {composition.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: e.color }} />
                    <span className="text-muted-foreground">{e.name}</span>
                    <span className="font-semibold tabular-nums ml-auto">{fmt(e.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-xs text-muted-foreground/60">Henüz etkileşim yok.</div>
          )}
        </ChartCard>
      </div>

      {/* Platform karşılaştırması — 5 hesap tek bakışta */}
      {byPlatform.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <ChartCard title="Platform karşılaştırması — gönderi hacmi">
            <div className="h-60 w-full" style={{ filter: "drop-shadow(0 0 8px rgba(34,211,238,0.12))" }}>
              <ResponsiveContainer>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="72%">
                  <PolarGrid stroke={GRID} />
                  <PolarAngleAxis dataKey="platform" tick={{ fill: AXIS, fontSize: 11, fontWeight: 500 }} />
                  <Radar dataKey="Gönderi" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.25} strokeWidth={2} animationDuration={800} dot={{ r: 3, fill: "#22d3ee" }} />
                  <RTooltip content={<ChartTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Platform tablosu (etkileşim yalnız X)">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] text-muted-foreground/60 text-left">
                    <th className="font-medium py-1.5 pr-2">Platform</th>
                    <th className="font-medium py-1.5 px-2 text-right">Gönderi</th>
                    <th className="font-medium py-1.5 px-2 text-right">Gösterim</th>
                    <th className="font-medium py-1.5 px-2 text-right">Etkileşim</th>
                    <th className="font-medium py-1.5 pl-2 text-right">Son</th>
                  </tr>
                </thead>
                <tbody>
                  {byPlatform.map((e, i) => {
                    const Icon = platformIcon(e.platform);
                    const meas = e.measured > 0;
                    return (
                      <tr key={i} className="border-t border-border/30">
                        <td className="py-2 pr-2">
                          <span className="flex items-center gap-2">
                            <span className="h-5 w-5 rounded-md flex items-center justify-center" style={{ background: (PLATFORM_COLOR[e.platform] || "#22d3ee") + "22" }}>
                              <Icon className="h-3 w-3" style={{ color: PLATFORM_COLOR[e.platform] || "#22d3ee" }} />
                            </span>
                            <span className="capitalize">{e.platform}</span>
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right font-semibold tabular-nums">{fmt(e.posts)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{meas ? fmt(e.impressions) : <span className="text-muted-foreground/40">—</span>}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{meas ? fmt(e.engagements) : <span className="text-muted-foreground/40">—</span>}</td>
                        <td className="py-2 pl-2 text-right text-[10px] text-muted-foreground/60">{relFromIso(e.last)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-muted-foreground/40 mt-2">"—" = o platformda etkileşim ölçümü yok (yalnız X).</p>
          </ChartCard>
        </div>
      )}

      {/* Per-post liste */}
      <div className="bento-card relative rounded-2xl md:rounded-3xl overflow-hidden bg-card/80 backdrop-blur-sm border border-border/40">
        <div className="h-[2px] bg-gradient-to-r from-cyan-500 via-emerald-500 to-teal-500" />
        <div className="divide-y divide-border/30">
          {loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Yükleniyor…</div>
          ) : posts.length === 0 ? (
            <div className="p-10 text-center">
              <Activity className="h-7 w-7 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground/70">Bu dönemde etkileşim verisi yok.</p>
            </div>
          ) : (
            posts.map((p, i) => {
              const Icon = platformIcon(p.platform);
              const m = p.metrics || {};
              const zero = p.measured && (m.impressions || 0) === 0;
              return (
                <div key={p.postiz_post_id || p.draft_id || i} className={cn("flex items-start gap-3 p-3.5 md:p-4", zero && "opacity-60")}>
                  <div className="shrink-0 h-8 w-8 rounded-lg bg-secondary/60 flex items-center justify-center mt-0.5" title={p.platform}>
                    <Icon className="h-3.5 w-3.5 text-foreground/70" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground/90 line-clamp-2 leading-snug">{p.topic || "(konu yok)"}</p>
                    {p.measured ? (
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2">
                        <Metric icon={BarChart3} value={m.impressions} pct={p.pct_change?.impressions} muted={zero} />
                        <Metric icon={Heart} value={m.likes} pct={p.pct_change?.likes} />
                        <Metric icon={Repeat2} value={m.retweets} pct={p.pct_change?.retweets} />
                        <Metric icon={MessageSquare} value={m.replies} pct={p.pct_change?.replies} />
                        <Metric icon={Quote} value={m.quotes} />
                        <Metric icon={Bookmark} value={m.bookmarks} />
                      </div>
                    ) : (
                      <p className="text-[10px] text-muted-foreground/45 mt-2">Etkileşim ölçümü yalnız X'te — bu platform için gönderi listelendi.</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] text-muted-foreground/50 capitalize">{p.platform}</span>
                      <span className="text-[10px] text-muted-foreground/30">·</span>
                      <span className="text-[10px] text-muted-foreground/50">{relFromIso(p.published_at)}</span>
                      {zero && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">ölçüm bekliyor</span>}
                    </div>
                  </div>
                  {p.release_url && (
                    <a
                      href={p.release_url}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 flex items-center gap-1 text-[11px] text-cyan-500 hover:text-cyan-400 transition-colors mt-0.5"
                    >
                      Canlı gör <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {data?.as_of && (
        <p className="text-[10px] text-muted-foreground/40 mt-3 px-1">
          Son ölçüm: {new Date(data.as_of).toLocaleString("tr-TR")} · Veri 6 saatte bir güncellenir.
        </p>
      )}
      </>
      )}
    </div>
  );
}
